import { useEffect, useState } from 'react';
import { EventDataType } from '@/types/EventDataType.type';
import { useAxios } from '@/hooks';
import { itemsOf } from '@/utils';
import { RootState } from '@/redux/store';
import { useSelector } from 'react-redux';

const useCategory = (dataEndpoint: string, method: string = 'POST') => {
  const location = useSelector((state: RootState) => state.location);
  const coordinates = [location?.lng, location?.lat];
  const [initialState, setInitialState] = useState<{
    limit: number;
    skip: number;
    refreshing: boolean;
    hasMore: boolean;
    data: EventDataType[] | null;
  }>({
    limit: 10,
    skip: 0,
    refreshing: false,
    hasMore: true,
    data: null,
  });
  const { limit, skip, refreshing, hasMore, data } = initialState;
  const { fetchData, loading } = useAxios(true);

  const updateState = (newState: any) => {
    setInitialState((prevState) => ({ ...prevState, ...newState }));
  };

  const fetchCategoryData = async ({
    limit = 10,
    skip = 0,
    loading = true,
  }: { limit?: number; skip?: number; loading?: boolean } = {}) => {
    try {
      const res = await fetchData({
        url: dataEndpoint,
        method,
        params: { limit, skip },
        data: { coordinates },
        loading,
      });

      if (res?.data) {
        // The listing answers `{ items, total, skip, limit }`. Reading
        // `res.data` directly assigned the envelope into the list.
        const events = itemsOf<EventDataType>(res.data);
        updateState({
          data: events,
          hasMore: events.length > 0 && events.length === limit,
        });
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  };

  const loadMore = async () => {
    if (!hasMore || refreshing || !data?.length) return;

    updateState({ refreshing: true });
    const nextSkip = skip + limit;

    try {
      const res = await fetchData({
        url: dataEndpoint,
        method,
        params: { limit, skip: nextSkip },
        data: { coordinates },
        loading: false,
      });

      const more = itemsOf<EventDataType>(res?.data);
      updateState({
        refreshing: false,
        skip: nextSkip,
        hasMore: more.length > 0 && more.length === limit,
        data: [...(data ?? []), ...more],
      });
    } catch (error) {
      console.error('Error fetching more category data:', error);
      updateState({ refreshing: false });
    }
  };

  useEffect(() => {
    updateState({ skip: 0, hasMore: true });
    fetchCategoryData({ limit: 10, skip: 0 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEndpoint, location]);

  return {
    data,
    loading,
    refreshing,
    hasMore,
    loadMore,
    refetch: fetchCategoryData,
  };
};

export default useCategory;
